<script setup lang="ts">
/**
 * The masthead: the company's mark and the name of the thing you are filling in.
 *
 * There is no navigation here on purpose. This is one flow with one way forward, so a nav bar
 * would only offer somewhere to get lost.
 *
 * The mark is whichever of two images we have. A client who has uploaded a logo in Settings gets
 * theirs, served from our own origin by `GET /api/branding/logo`; everyone else gets the built-in
 * Altamont mark, the one copy of the drawing. Both sit on a white tile, because the drawing is
 * navy ink and a client's logo is usually made for paper — neither can be trusted to read on the
 * navy ground. Both are a fixed 40px tall inside a box that is already that size before either
 * loads, so nothing moves under the reader.
 *
 * A logo that fails to load falls back to the built-in mark rather than leaving a broken-image
 * icon in the masthead of a page someone is about to trust with a video of their face.
 *
 * The words stay either way: the `alt` text is "Altamont" and the words beside it are "Video
 * Application", so a screen reader reads the whole name once — "Altamont Video Application"
 * (docs/DESIGN.md §6.6). A logo is a picture of a name, not a title.
 */

import { computed, ref, watch } from 'vue';

import markUrl from '@/assets/altamont-mark.svg';
import { useConfigStore } from '@/stores/config';

const config = useConfigStore();

/** Set once this browser has actually tried the client's logo and been handed nothing usable. */
const failed = ref(false);

/** The client's logo, or `null` — no logo configured, or the one configured did not load. */
const logoSrc = computed<string | null>(() => (failed.value ? null : config.logoUrl));

// A new address is a new chance: an admin who fixes a bad upload should not have to reload.
watch(
  () => config.logoUrl,
  () => {
    failed.value = false;
  },
);

function onLogoError(): void {
  failed.value = true;
}
</script>

<template>
  <header class="alt-header">
    <div class="alt-header__inner">
      <span class="alt-header__mark">
        <img
          v-if="logoSrc"
          class="alt-header__logo"
          :src="logoSrc"
          alt="Altamont"
          height="40"
          @error="onLogoError"
        />
        <img v-else class="alt-header__logo" :src="markUrl" alt="Altamont" width="40" height="40" />
      </span>
      <span class="alt-header__name">Video Application</span>
    </div>
  </header>
</template>

<style scoped>
.alt-header {
  background-color: var(--alt-navy);
  color: var(--alt-white);
  border-bottom: 1px solid var(--alt-navy-700);
}

/* Same column as the screens below it: a phone-width band that widens once there is room. */
.alt-header__inner {
  display: flex;
  align-items: center;
  gap: var(--alt-space-3);
  width: 100%;
  max-width: var(--alt-maxw-phone);
  margin-inline: auto;
  padding: var(--alt-space-3) var(--alt-space-5);
}

@media (min-width: 720px) {
  .alt-header__inner {
    max-width: var(--alt-maxw-content);
    padding: var(--alt-space-4) var(--alt-space-7);
  }
}

/* The mark is navy ink, so it needs a light tile to sit on — and so does most printed logo art.
   The tile holds its 40px square whether the image has arrived or not. */
.alt-header__mark {
  display: inline-flex;
  min-width: 40px;
  min-height: 40px;
  flex: none;
  align-items: center;
  justify-content: center;
  padding: var(--alt-space-1);
  border-radius: var(--alt-radius);
  background-color: var(--alt-white);
}

/* Height is fixed and width follows the artwork, so a wide logo stays legible and a tall one
   cannot push the masthead open. The cap stops a banner-shaped logo eating the whole band. */
.alt-header__logo {
  width: auto;
  height: 40px;
  min-width: 40px;
  max-width: 180px;
  object-fit: contain;
}

.alt-header__name {
  font-size: var(--alt-text-lg);
  font-weight: 700;
  line-height: 1.2;
  letter-spacing: 0.01em;
}

@media (min-width: 720px) {
  .alt-header__name {
    font-size: var(--alt-text-xl);
  }
}
</style>
